/**
 * Component display name — the one naming rule shared by web, iOS and the
 * guest view. Mirrors:
 *   iOS        Husappen/Building/Models/Components/BaseComponent.swift (displayName)
 *   functions  src/guestAccess/componentNaming.ts (resolveComponentName)
 * Keep the three in sync.
 *
 * Precedence: custom label → window number → "shape, type" → base type name,
 * then " (direction)" when a cardinal direction is set. The shape comes first
 * because it says more about a roof than its covering ("Sadeltak, betongpannor").
 * An unresolvable specific type is left out, never shown as a placeholder word.
 */
export interface ComponentNameParts {
    /** Custom label; replaces the derived name entirely. */
    label?: string | null;
    /** True for windows, which are numbered. */
    isWindow: boolean;
    sequentialNumber?: number | null;
    /** Translated roof shape, if the component has one. */
    roofShapeName?: string | null;
    /** Translated specific type, or undefined when the key can't be resolved. */
    specificTypeName?: string | null;
    /** Translated base type ("Tak", "Fasad", ...). */
    baseTypeName: string;
    /** Translated cardinal direction, if set. */
    cardinalDirectionName?: string | null;
}

/** "Betongpannor" → "betongpannor"; leaves acronyms such as "PVC" alone. */
export const lowercaseFirst = (word: string): string => {
    if (word.length < 2) return word.toLowerCase();
    const second = word.charAt(1);
    if (second !== second.toLowerCase()) return word;
    return word.charAt(0).toLowerCase() + word.slice(1);
};

export const composeComponentDisplayName = (parts: ComponentNameParts): string => {
    let name: string;
    if (parts.label) {
        name = parts.label;
    } else if (parts.isWindow && parts.sequentialNumber) {
        return `Fönster ${parts.sequentialNumber}`;
    } else {
        const words: string[] = [];
        if (parts.roofShapeName) words.push(parts.roofShapeName);
        if (parts.specificTypeName) {
            words.push(words.length > 0 ? lowercaseFirst(parts.specificTypeName) : parts.specificTypeName);
        }
        name = words.length > 0 ? words.join(', ') : parts.baseTypeName;
    }

    if (parts.cardinalDirectionName) {
        name = `${name} (${parts.cardinalDirectionName})`;
    }
    return name;
};
